import { Injectable, inject } from '@angular/core';
import { BaseApiService, PaginatedResults } from '@bo/ng-api';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AccountConnectionDetail,
  AccountingAccount,
  AccountingAccountType,
  AccountingConnection,
  AccountingConnectionRequest,
  AccountingContact,
  AccountingProvider,
  AccountingSyncType,
  AccountingSync,
  TenantAccountOption,
  AccountingTrackingCategory,
  AccountingTaxRates,
} from '../../../_interfaces/accounting.interface';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountingApiService extends BaseApiService {
  protected override http: HttpClient;

  override baseUrl: string = environment.apiRoot;

  URL_ACCOUNTING_CONNECTION: string = 'accounting/connection';
  URL_ACCOUNTING_ACCOUNT: string = 'accounting/account';
  URL_ACCOUNTING_PROVIDER: string = 'accounting/provider';
  URL_ACCOUNTING_TENANT: string = 'accounting/tenant';
  URL_ACCOUNTING_SYNC: string = 'accounting/sync';
  URL_ACCOUNTING_CONTACT: string = 'accounting/contact';
  URL_ACCOUNTING_TRACKING_CATEGORIES: string = 'accounting/tracking-categories';
  URL_ACCOUNTING_TAX_RATES: string = 'accounting/tax-rates';
  URL_SELECT: string = 'select'; // relative to URL_ACCOUNTING_TENANT
  URL_SOURCE_DEPOSITS: string = 'source-deposits'; // relative to URL_ACCOUNTING_ACCOUNT

  constructor() {
    const http = inject(HttpClient);

    super(http);

    this.http = http;
  }

  /**
   * Get connection url for the specified provider
   *
   * @param provider
   * @return Observable<AccountingConnection>
   */
  requestAccountingConnectionUrl(provider: AccountingConnectionRequest): Observable<AccountingConnection> {
    const url = this.getUrl(this.URL_ACCOUNTING_CONNECTION, true);
    return this.http.post<AccountingConnection>(url, provider);
  }

  /**
   * Get account connection details, for the redirect page
   *
   * @return Observable<AccountConnectionDetail>
   */
  getAccountConnectionDetails(): Observable<AccountConnectionDetail> {
    const url = this.getUrl(this.URL_ACCOUNTING_CONNECTION, true);
    return this.http.get<AccountConnectionDetail>(url);
  }

  /**
   * Delete account connection
   *
   * @return Observable<AccountConnectionDetail>
   */
  deleteAccountConnection(): Observable<AccountConnectionDetail> {
    const url = this.getUrl(this.URL_ACCOUNTING_CONNECTION, true);
    return this.http.delete<AccountConnectionDetail>(url);
  }

  /**
   * Get connection provider details
   *
   * @return Observable<AccountingProvider[]>
   */
  getAccountingProviders(): Observable<AccountingProvider[]> {
    const url = this.getUrl(this.URL_ACCOUNTING_PROVIDER, true);
    return this.http.get<AccountingProvider[]>(url);
  }

  /**
   * Get Contra account details
   *
   * @return Observable<AccountingAccount[]>
   */
  getAccountingContraAccount(): Observable<AccountingAccount[]> {
    const params = new HttpParams().append('type', AccountingAccountType.Contra);
    const url = this.getUrl(this.URL_ACCOUNTING_ACCOUNT, true);
    return this.http.get<AccountingAccount[]>(url, { params: params });
  }

  /**
   * Get Bank Source account details
   *
   * @return Observable<AccountingAccount[]>
   */
  getAccountingSourceAccount(): Observable<AccountingAccount[]> {
    const params = new HttpParams().append('type', AccountingAccountType.Bank);
    const url = this.getUrl(this.URL_ACCOUNTING_ACCOUNT, true);
    return this.http.get<AccountingAccount[]>(url, { params: params });
  }

  /**
   * Get tenant options
   *
   * @return Observable<TenantAccountOption[]>
   */
  getTenantAccountOptions(): Observable<TenantAccountOption[]> {
    const url = this.getUrl(this.URL_ACCOUNTING_TENANT, true);
    return this.http.get<TenantAccountOption[]>(url);
  }

  /**
   * Send a selected tenant option
   */
  updateTenant(tenantId: number) {
    const url = this.getUrl([this.URL_ACCOUNTING_TENANT, tenantId, this.URL_SELECT], true);
    return this.http.put<number>(url, {});
  }

  /**
   * Send a selected source account option
   */
  updateSourceAccount(accountId: number) {
    const url = this.getUrl([this.URL_ACCOUNTING_ACCOUNT, accountId, this.URL_SOURCE_DEPOSITS], true);
    return this.http.put<number>(url, {});
  }

  /**
   * Get accounting contacts with pagination and search support
   *
   * @param page - Page number (1-based)
   * @param pageSize - Number of items per page
   * @param searchTerm - Optional search term to filter by contact name
   * @return Observable<PaginatedResults<AccountingContact>>
   */
  getAccountingContacts(
    page: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
  ): Observable<PaginatedResults<AccountingContact>> {
    let params = new HttpParams().set('page', page.toString()).set('page_size', pageSize.toString());

    if (searchTerm?.trim()) {
      params = params.set('name', searchTerm.trim());
    }

    const url = this.getUrl(this.URL_ACCOUNTING_CONTACT, true);
    return this.http.get<AccountingContact>(url, { observe: 'response', params }).pipe(
      map((res) => {
        return new PaginatedResults<AccountingContact>(res);
      }),
    );
  }

  /**
   * Get the tracking categories from Xero
   *
   * @return Observable<AccountingTrackingCategory[]>
   */
  getAccountingTrackingCategories(): Observable<AccountingTrackingCategory[]> {
    const url = this.getUrl(this.URL_ACCOUNTING_TRACKING_CATEGORIES, true);
    return this.http.get<AccountingTrackingCategory[]>(url);
  }

  /**
   * Send a request to sync with accounting software. Specify the sync type.
   *
   * @param type - The type of sync to perform (Transactions, Contacts, or Categories)
   * @return Observable<AccountingSync>
   */
  requestAccountingSync(type: AccountingSyncType): Observable<AccountingSync> {
    const url = this.getUrl(this.URL_ACCOUNTING_SYNC, true);
    return this.http.put<AccountingSync>(url, { type: type });
  }

  /**
   * Get the current accounting sync status
   *
   * @return Observable<AccountingSync>
   */
  getAccountingSyncStatus(): Observable<AccountingSync> {
    const url = this.getUrl(this.URL_ACCOUNTING_SYNC, true);
    return this.http.get<AccountingSync>(url);
  }

  /**
   * Get the accounting tax rates
   *
   * @return Observable<AccountingTaxRates[]>
   */
  getAccountingTaxRates(): Observable<AccountingTaxRates[]> {
    const url = this.getUrl(this.URL_ACCOUNTING_TAX_RATES, true);
    return this.http.get<AccountingTaxRates[]>(url);
  }
}
